import { createContext, useContext, type ReactNode } from 'react';
import type { NewReportData } from '../types';
import type { ReportMetricCalculation, ReportMetricDefinition } from './types';
import { useReportDefinition, type ReportDefinitionController } from './useReportDefinition';

const NO_CALCULATIONS: ReportMetricCalculation[] = [];
const NO_METRICS: ReportMetricDefinition[] = [];

/** The whole editor controller: changes on every draft edit, so only editor-facing components may read it. */
const ControllerContext = createContext<ReportDefinitionController | null>(null);
/** Metric definitions of the current document; stable while only titles, layout or tables are edited. */
const MetricsContext = createContext<ReportMetricDefinition[]>(NO_METRICS);
/** Valid metric calculations feeding the answer-group pipeline; stable until a calculation itself changes. */
const CalculationsContext = createContext<ReportMetricCalculation[]>(NO_CALCULATIONS);

/**
 * Owns the session-local draft above the report routes. Keeping it out of the `report` object means a keystroke
 * in a section title re-renders the overview and the editor dock, not the header, the menu or the filters.
 */
export function ReportDefinitionProvider({
  report,
  scope,
  basePath,
  children,
}: {
  report: NewReportData | undefined;
  scope: string;
  basePath?: string;
  children: ReactNode;
}) {
  const controller = useReportDefinition(report, scope, basePath);
  return (
    <CalculationsContext.Provider value={controller.metrics}>
      <MetricsContext.Provider value={controller.document.metrics}>
        <ControllerContext.Provider value={controller}>{children}</ControllerContext.Provider>
      </MetricsContext.Provider>
    </CalculationsContext.Provider>
  );
}

export function useDefinition(): ReportDefinitionController {
  const controller = useContext(ControllerContext);
  if (!controller) throw new Error('useDefinition must be used inside ReportDefinitionProvider');
  return controller;
}

export const useDefinitionMetrics = () => useContext(MetricsContext);

export const useDefinitionCalculations = () => useContext(CalculationsContext);
